'use client'

import * as React from 'react'
import { Twitter } from 'lucide-react'

// Shows a "Share quote" tooltip when the reader selects text inside the
// article. Positions itself just above the selection using fixed coords so
// scroll offset doesn't affect it. The tweet pre-fills the selected sentence
// as a block-quote with the article title and URL appended.

export function TextHighlightShare({
  title,
  slug,
}: {
  title: string
  slug: string
}) {
  const [tooltip, setTooltip] = React.useState<{
    text: string
    x: number
    y: number
  } | null>(null)

  React.useEffect(() => {
    function onSelectionChange() {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setTooltip(null)
        return
      }
      const text = sel.toString().trim()
      // Ignore very short selections and anything that would blow the tweet limit
      if (text.length < 15 || text.length > 240) {
        setTooltip(null)
        return
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect()
      setTooltip({
        text,
        x: rect.left + rect.width / 2,
        y: rect.top,
      })
    }

    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [])

  if (!tooltip) return null

  const url = `https://idukki.io/blog/${slug}`
  const tweet = `"${tooltip.text}"\n\n— ${title}\n${url}`
  const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`

  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: tooltip.x,
        top: tooltip.y,
        transform: 'translate(-50%, calc(-100% - 10px))',
        zIndex: 50,
      }}
      className="pointer-events-auto animate-in fade-in zoom-in-95 duration-150"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // Prevent mousedown from collapsing the selection before the click fires
        onMouseDown={(e) => e.preventDefault()}
        className="flex items-center gap-1.5 rounded-full bg-[#1c2142] px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10 transition hover:text-brand-lime"
      >
        <Twitter className="h-3 w-3 shrink-0" />
        Share quote
      </a>
    </div>
  )
}
